/**
 * Owner: Masa Bayu
 * File: text-note.ts
 * Date:Time: 2026-09-20 21:45
 * Description: CA-03 B7 / CA-08 A4 footnote · endnote · note-ref identity.
 */
export const QX_TEXT_NOTE_BLOCK_TYPES = ['footnote', 'endnote'];
export function isQXTextNoteBlockType(value) {
    return QX_TEXT_NOTE_BLOCK_TYPES.includes(value);
}
export function isQXTextNoteBlock(block) {
    return isQXTextNoteBlockType(block.type);
}
export function isQXTextNoteRefInline(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    return value.type === 'note-ref';
}
function requireNoteId(id) {
    if (typeof id !== 'string' || id.trim().length === 0) {
        throw new Error('note id must be a non-empty string');
    }
    return id;
}
function defaultInlines() {
    return [{ type: 'text', text: '', marks: [] }];
}
function copyInlines(inlines) {
    return [...(inlines ?? defaultInlines())];
}
export function createQXTextFootnoteBlock(input) {
    const block = {
        type: 'footnote',
        id: requireNoteId(input.id),
        inlines: copyInlines(input.inlines),
    };
    if (input.language !== undefined) {
        block.language = input.language;
    }
    return Object.freeze(block);
}
export function createQXTextEndnoteBlock(input) {
    const block = {
        type: 'endnote',
        id: requireNoteId(input.id),
        inlines: copyInlines(input.inlines),
    };
    if (input.language !== undefined) {
        block.language = input.language;
    }
    return Object.freeze(block);
}
export function createQXTextNoteRefInline(targetRef) {
    return Object.freeze({
        type: 'note-ref',
        targetRef: requireNoteId(targetRef),
    });
}
/** Collect note ids present in the content model (same publication content scope). */
export function collectQXTextNoteIds(model) {
    const ids = new Set();
    for (const block of model.blocks) {
        if (isQXTextNoteBlock(block)) {
            ids.add(block.id);
        }
    }
    return ids;
}
export function insertQXTextNoteBlock(model, blockIndex, note) {
    if (blockIndex < 0 || blockIndex > model.blocks.length) {
        throw new Error('note blockIndex out of range');
    }
    const frozen = note.type === 'footnote'
        ? createQXTextFootnoteBlock(note)
        : createQXTextEndnoteBlock(note);
    if (collectQXTextNoteIds(model).has(frozen.id)) {
        throw new Error(`duplicate note id "${frozen.id}"`);
    }
    const blocks = [...model.blocks];
    blocks.splice(blockIndex, 0, frozen);
    return { ...model, blocks };
}
export function deleteQXTextNoteBlock(model, blockIndex) {
    const block = model.blocks[blockIndex];
    if (block === undefined || !isQXTextNoteBlock(block)) {
        throw new Error('expected footnote or endnote at blockIndex');
    }
    const blocks = model.blocks.filter((_, index) => index !== blockIndex);
    return { ...model, blocks };
}
/**
 * Insert a note-ref into a leaf/list-item inline stream.
 * Fail-closed when target note id is absent from the model.
 */
export function insertQXTextNoteRef(model, blockIndex, inlineIndex, targetRef, itemIndex) {
    const noteIds = collectQXTextNoteIds(model);
    if (!noteIds.has(targetRef)) {
        throw new Error(`orphan note-ref: target "${targetRef}" is not a note in content scope`);
    }
    const ref = createQXTextNoteRefInline(targetRef);
    const block = model.blocks[blockIndex];
    if (block === undefined) {
        throw new Error('blockIndex out of range');
    }
    if (block.type === 'list') {
        if (itemIndex === undefined) {
            throw new Error('list note-ref requires itemIndex');
        }
        const item = block.items[itemIndex];
        if (item === undefined) {
            throw new Error('list itemIndex out of range');
        }
        if (inlineIndex < 0 || inlineIndex > item.inlines.length) {
            throw new Error('inlineIndex out of range');
        }
        const inlines = [...item.inlines];
        inlines.splice(inlineIndex, 0, ref);
        const items = block.items.map((entry, index) => index === itemIndex ? { ...entry, inlines } : entry);
        const blocks = model.blocks.map((entry, index) => index === blockIndex ? { ...block, items } : entry);
        return { ...model, blocks };
    }
    if (block.type === 'figure' ||
        !('inlines' in block)) {
        throw new Error('note-ref requires a leaf (or list-item) inline host');
    }
    if (inlineIndex < 0 || inlineIndex > block.inlines.length) {
        throw new Error('inlineIndex out of range');
    }
    const inlines = [...block.inlines];
    inlines.splice(inlineIndex, 0, ref);
    const blocks = model.blocks.map((entry, index) => index === blockIndex ? { ...block, inlines } : entry);
    return { ...model, blocks };
}
/** Plain-text contribution of a note-ref (identity-only · no fabricated marker text). */
export function plainTextFromNoteRef(_inline) {
    return '';
}
//# sourceMappingURL=text-note.js.map